/* @jsx h */
// @ts-nocheck
import { computed, defineComponent, h, ref, watch } from "vue";
import {
  FastQuantitySelector,
  LemmonModal,
  useLocalization,
} from "@lemmonhq/uikit";
import { useAuth } from "@app/vue/providers/Auth";
import { useToast } from "@app/vue/providers/Toast";
import { api } from "@app/data/api";
import { useTrpc } from "@app/vue/providers/TrpcProvider";
import { listReservations } from "../../../utils/trpcReservations";
import {
  FloorPlan,
  Reservation,
  TableStatus,
  type TableSelectorTable,
} from "@lemmonhq/types";
import { TableSelectionPanel } from "../../ReservationTabs/TableSelectionPanel";
import { getRestaurantDateKey } from "@lemmonhq/blogic";
import { ReservationDurationField } from "./ReservationDurationField";
import { timeInTimezoneToUTC } from "@app/vue/pages/ManagerReservations/utils/shiftHelpers";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";

dayjs.extend(utc);
dayjs.extend(timezone);

interface EditReservationModalProps {
  isOpen: boolean;
  reservation: Reservation;
  onClose: () => void;
  onSuccess: (reservation: Reservation) => void;
}

export const EditReservationModal = defineComponent<EditReservationModalProps>({
  name: "EditReservationModal",
  props: ["isOpen", "reservation", "onClose", "onSuccess"],
  setup(props) {
    const { user } = useAuth();
    const { t } = useLocalization();
    const toast = useToast();
    const trpc = useTrpc();
    const restaurantTimeZone = computed(
      () => user.value?.restaurant?.timezone || "UTC",
    );

    const toDateValue = (value: string) =>
      dayjs(value).tz(restaurantTimeZone.value).format("YYYY-MM-DD");
    const toTimeValue = (value: string) =>
      dayjs(value).tz(restaurantTimeZone.value).format("HH:mm");

    const selectedDate = ref(toDateValue(props.reservation.time));
    const selectedTime = ref(toTimeValue(props.reservation.time));
    const partySize = ref(props.reservation.party_size);
    const durationMins = ref(props.reservation.duration_mins);
    const selectedTableIds = ref<number[]>(
      (props.reservation.table_assignments || []).map((a) => a.table_id),
    );
    const allTables = ref<TableStatus[]>([]);
    const dayReservations = ref<Reservation[]>([]);
    const floorPlans = ref<FloorPlan[]>([]);
    const loadingTables = ref(false);
    const saving = ref(false);
    const error = ref("");
    const tablesError = ref("");

    const minDate = computed(() => {
      return getRestaurantDateKey(new Date(), restaurantTimeZone.value);
    });

    const dateTimeReady = computed(
      () =>
        !!selectedDate.value &&
        !!selectedTime.value &&
        selectedTime.value.trim() !== "",
    );

    const loadTablesData = async () => {
      if (!selectedDate.value || !props.isOpen) return;

      try {
        loadingTables.value = true;
        tablesError.value = "";
        const results = await Promise.all([
          api.getTablesMeta(),
          listReservations(trpc, {
            date: selectedDate.value,
            timezone: restaurantTimeZone.value,
            limit: "200",
          }),
          api.getFloorPlans(),
        ]);
        allTables.value = results[0] || [];
        dayReservations.value = (
          (results[1] as { data: Reservation[] }).data || []
        ).filter((reservation) => reservation.id !== props.reservation.id);
        floorPlans.value = results[2] || [];
      } catch (e) {
        console.error("Failed to load tables data:", e);
        tablesError.value = "Failed to load tables data";
      } finally {
        loadingTables.value = false;
      }
    };

    watch(
      () => props.isOpen,
      (isOpen) => {
        if (!isOpen) return;

        selectedDate.value = toDateValue(props.reservation.time);
        selectedTime.value = toTimeValue(props.reservation.time);
        partySize.value = props.reservation.party_size;
        durationMins.value = props.reservation.duration_mins;
        selectedTableIds.value = (
          props.reservation.table_assignments || []
        ).map((assignment) => assignment.table_id);
        error.value = "";
        loadTablesData();
      },
      { immediate: true },
    );

    watch(selectedDate, () => {
      if (props.isOpen) {
        loadTablesData();
      }
    });

    const tablesWithAvailability = computed(() => {
      if (allTables.value.length === 0) return [];

      if (!dateTimeReady.value) {
        return allTables.value.map((table) => ({
          table,
          reservations: [] as Reservation[],
          isAvailable: true,
          hasConflict: false,
        }));
      }

      const requestedStart = timeInTimezoneToUTC(
        selectedDate.value,
        selectedTime.value,
        restaurantTimeZone.value,
      );
      const requestedEnd = new Date(
        requestedStart.getTime() + durationMins.value * 60000,
      );

      return allTables.value
        .map((table) => {
          const tableReservations = dayReservations.value.filter(
            (reservation) =>
              reservation.table_assignments?.some(
                (assignment) => assignment.table_id === table.id,
              ),
          );
          const hasConflict = tableReservations.some((reservation) => {
            const resStart = new Date(reservation.time);
            const resEnd = new Date(
              resStart.getTime() + reservation.duration_mins * 60000,
            );
            return requestedStart < resEnd && requestedEnd > resStart;
          });

          return {
            table,
            reservations: tableReservations,
            isAvailable: !hasConflict,
            hasConflict,
          };
        })
        .sort((a, b) => {
          if (a.isAvailable !== b.isAvailable) return a.isAvailable ? -1 : 1;
          return a.table.table.name.localeCompare(b.table.table.name);
        });
    });

    const getRoom = (tableId: number) => {
      for (const fp of floorPlans.value) {
        if (fp.plan?.some((t) => t.tableId === tableId)) {
          return { name: fp.name, rank: fp.rank ?? 999 };
        }
      }

      return { name: undefined, rank: 999 };
    };

    const tableSelectorData = computed<TableSelectorTable[]>(() =>
      tablesWithAvailability.value.map(
        ({ table, hasConflict, reservations }) => ({
          id: table.id,
          name: table.table.name,
          roomName: getRoom(table.id).name,
          roomRank: getRoom(table.id).rank,
          availability: hasConflict ? "busy" : "free",
          reservationsCount: reservations.length,
          disabled: saving.value,
        }),
      ),
    );

    const availableTablesCount = computed(
      () =>
        tablesWithAvailability.value.filter((table) => table.isAvailable)
          .length,
    );

    const canSubmit = computed(
      () =>
        !!selectedDate.value &&
        !!selectedTime.value &&
        partySize.value > 0 &&
        !saving.value,
    );

    const handleSubmit = async () => {
      if (!canSubmit.value) return;

      try {
        saving.value = true;
        error.value = "";

        const dateTimeUtc = timeInTimezoneToUTC(
          selectedDate.value,
          selectedTime.value,
          restaurantTimeZone.value,
        );

        const result = await trpc.reservations.change.mutate({
          reservation_id: props.reservation.id,
          time: dateTimeUtc.toISOString(),
          party_size: partySize.value,
          duration_mins: durationMins.value,
          table_ids: selectedTableIds.value,
        });

        toast.showToast("Reservation changed", { type: "success" });
        props.onSuccess(result.reservation as Reservation);
        props.onClose();
      } catch (e) {
        console.error("Failed to change reservation:", e);
        error.value =
          e instanceof Error ? e.message : "Failed to change reservation";
        toast.showToast(error.value, { type: "error" });
      } finally {
        saving.value = false;
      }
    };

    return () =>
      !props.isOpen ? null : (
        <LemmonModal
          title="Edit Reservation"
          onClose={props.onClose}
          renderButtons={() => (
            <div class="p-4 border-t border-base-content/10">
              <div class="flex justify-between gap-2">
                <button
                  type="button"
                  class="btn btn-neutral"
                  onClick={props.onClose}
                  disabled={saving.value}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  class="btn btn-primary"
                  onClick={handleSubmit}
                  disabled={!canSubmit.value}
                >
                  {saving.value ? (
                    <span class="loading loading-spinner loading-xs" />
                  ) : null}
                  Change
                </button>
              </div>
            </div>
          )}
        >
          <div class="px-6 py-5 space-y-5">
            <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
              <label class="form-control">
                <span class="label-text text-xs font-semibold uppercase tracking-wider text-accent mb-2">
                  Date
                </span>
                <input
                  type="date"
                  class="input input-bordered w-full"
                  min={minDate.value}
                  value={selectedDate.value}
                  onInput={(e: any) => {
                    selectedDate.value = e.target.value;
                  }}
                  disabled={saving.value}
                />
              </label>

              <label class="form-control">
                <span class="label-text text-xs font-semibold uppercase tracking-wider text-accent mb-2">
                  Time
                </span>
                <input
                  type="time"
                  step="900"
                  class="input input-bordered w-full"
                  value={selectedTime.value}
                  onInput={(e: any) => {
                    selectedTime.value = e.target.value;
                  }}
                  disabled={saving.value}
                />
              </label>
            </div>

            <div class="space-y-4">
              <label class="form-control">
                <span class="label-text text-xs font-semibold uppercase tracking-wider text-accent mb-2">
                  Guests
                </span>
                <FastQuantitySelector
                  value={partySize.value > 0 ? partySize.value : null}
                  onChange={(value) => {
                    partySize.value = value ?? 0;
                  }}
                  disabled={saving.value}
                />
              </label>

              <div>
                <span class="label-text text-xs font-semibold uppercase tracking-wider text-accent mb-2 block">
                  Duration
                </span>
                <div class="h-12 px-3 rounded-xl   flex items-center">
                  <div class="flex items-center gap-2 flex-1">
                    <ReservationDurationField
                      value={durationMins.value}
                      onChange={(value) => {
                        durationMins.value = value;
                      }}
                      disabled={saving.value}
                    />
                  </div>
                </div>
              </div>
            </div>

            <TableSelectionPanel
              tables={tableSelectorData.value}
              selectedIds={selectedTableIds.value}
              onChange={(ids) => {
                selectedTableIds.value = ids;
              }}
              loading={loadingTables.value}
              error={tablesError.value}
              onRetry={loadTablesData}
              availableCount={availableTablesCount.value}
              totalCount={allTables.value.length}
              selectionDisabled={saving.value}
            />

            {error.value ? (
              <div class="rounded-lg border border-error/20 bg-error/10 px-3 py-2 text-sm text-error">
                {error.value}
              </div>
            ) : null}
          </div>
        </LemmonModal>
      );
  },
});
